<template>
  <div>
    <div>
      <router-link
        v-if="profileLink"
        :to="{ name: 'HorseProfilePage', params: { horseId: horse.id } }"
        class="no-default-link-style"
        :target="linkTarget"
      >
        {{ displayName }}
      </router-link>

      <span v-else>{{ displayName }}</span>

      <template v-if="horse.ponyType">
        |
        <AppShowHorsePonyStatus
          :horse="horse"
          :start-date="startDateForPonyMeasurementValidate"
        />
      </template>
      <span>{{ showRegistryNumber }}</span>
      <span
        >&nbsp;|
        {{
          horse.ageForSeason
            ? horse.ageForSeason +
              $t(
                "horses:appShowHorse.age.suffix",
                "y.",
                "Suffix to show after horse age"
              )
            : ""
        }}</span
      >
    </div>

    <div>
      <slot name="appendHorseDetails" :horse="horse" />
    </div>

    <div class="muted--text" v-html="displayDetails" />
    <div v-if="detailsType == 'full'">
      {{
        $t(
          "horses:appShowHorse.Licence.field.label",
          "Licence:",
          "Licence field label for horse"
        )
      }}
      <AppValidationAlert
        :check-valid="horse.currentSeasonLicenceValid"
        :valid="
          $t(
            'horses:appshow.licence.valid.label',
            'Valid',
            'Label for valid yearly licence status'
          )
        "
        :invalid="
          $t(
            'horses:appshow.licence.invalid.label',
            'Not valid',
            'Label for invalid yearly licence status'
          )
        "
      />
      <div>
        {{
          $t(
            "horses:appShowHorse.vaccination.field.label",
            "Vaccination:",
            "Vaccination field label for horse"
          )
        }}

        <AppShowHorseVaccinationStatus
          :horse="horse"
          :check-date="vaccinationCheckDate"
        />
        <slot name="showVaccinations" :horse="horse" />
      </div>
    </div>
  </div>
</template>
<script>
import { defineComponent } from "vue";
import gql from "fraql";
import AppValidationAlert from "@elements/AppValidationAlert.vue";
import AppShowHorseVaccinationStatus from "./AppShowHorseVaccinationStatus.vue";
import AppShowHorsePonyStatus from "./AppShowHorsePonyStatus.vue";

export default defineComponent({
  name: "AppShowHorseWithDetails",
  components: {
    AppValidationAlert,
    AppShowHorseVaccinationStatus,
    AppShowHorsePonyStatus
  },
  props: {
    horse: {
      type: Object,
      required: true
    },
    ignorePonyState: {
      type: Boolean,
      default: false
    },
    startDateForPonyMeasurementValidate: {
      type: String,
      default: null
    },
    showIdentifier: {
      type: Boolean,
      default: false
    },
    profileLink: {
      type: Boolean,
      default: false
    },
    linkTarget: {
      type: String,
      default: undefined
    },
    detailsType: {
      type: String,
      default: "default"
    },
    showWithRegistry: {
      type: String,
      default: null
    },
    detailsToShow: {
      type: Array,
      default() {
        return [];
      }
    },
    vaccinationCheckDate: {
      type: String,
      default: undefined
    }
  },
  computed: {
    showRegistryNumber() {
      let registry = "";
      if (this.showWithRegistry) {
        if (this.showWithRegistry === "western") {
          registry += " | Western ID " + this.horse.westernRegistryNumber;
        } else if (this.showWithRegistry === "icelandic") {
          registry += " | FEIF ID " + this.horse.icelandicRegistryNumber;
        }
      }
      return registry;
    },
    displayName() {
      let nameDetails = this.horse.name;
      if (this.showIdentifier) {
        nameDetails += ` | ${this.horse.activeIdentifier}`;
      }
      if (this.horse.feiid) {
        nameDetails += ` | ${this.horse.feiid}`;
      }
      return nameDetails;
    },
    displayDetails() {
      let horseDetails = "";

      let detailsToShow =
        this.detailsToShow.length == 0
          ? [
              [
                "dateOfBirth",
                "gender",
                "breedId",
                "sireName",
                "damName",
                "damSireName",
                "birthCountryId",
                "ownerName"
              ]
            ]
          : this.detailsToShow;
      let additionalDetails = [
        "identifier",
        "westernRegistryNumber",
        "icelandicRegistryNumber"
      ];

      if (this.detailsToShow.length == 0 && this.detailsType == "full") {
        detailsToShow[1] = additionalDetails;
      } else if (this.detailsType == "western") {
        detailsToShow[0].push("westernRegistryNumber");
      } else if (this.detailsType == "icelandic") {
        detailsToShow[0].push("icelandicRegistryNumber");
      }
      detailsToShow.forEach((details, index) => {
        if (index > 0) horseDetails += `<br>`;
        details.forEach((detail, i) => {
          if (i != 0) horseDetails += " / ";
          // If null, skip
          if (!this.horse[detail] && detail != "breedId") return;
          switch (detail) {
            case "identifier":
              horseDetails += this.horse[detail];
              if (this.horse.identifierHeppaStatus == "active")
                horseDetails += " | HEPPA";
              break;
            case "breedId":
              if (this.$federation.countryCode === "BE") {
                horseDetails +=
                  this.horse?.breedingStudbook?.nameShort ??
                  this.horse.breed?.nameShort ??
                  "";
              } else {
                horseDetails += this.horse.breed?.nameShort ?? "";
              }

              break;
            case "dateOfBirth":
              horseDetails += this.$dayjs(this.horse[detail]).year();
              break;
            case "westernRegistryNumber":
              horseDetails += " Western ID " + this.horse[detail];
              break;
            case "icelandicRegistryNumber":
              horseDetails += " FEIF ID " + this.horse[detail];
              break;
            case "birthCountryId":
              horseDetails += this.horse.birthCountry?.ioc ?? "";
              break;
            default:
              horseDetails += this.horse[detail];
          }
        });
      });

      return horseDetails;
    }
  },
  fragments: {
    horse: gql`
     fragment _ on Horse {
      id
      name
      identifier
      identifierHeppaStatus
      activeIdentifier
      feiid

      westernRegistryNumber
      icelandicRegistryNumber

      ${AppShowHorsePonyStatus.fragments.horseFields}
      currentSeasonLicenceValid
      ageForSeason

      dateOfBirth
      gender

      breedingStudbookId
      breedingStudbook @client {
        id
        nameShort
      }
      breedId
      breed @client {
        id
        name
        nameShort
      }

      sireName
      damName
      damSireName
      birthCountryId
      birthCountry @client {
        id
        name
        ioc
        alpha2
      }
      ownerName
    }
    `,
    horseForRegistration: gql`
      fragment _ on HorseForRegistration {
        id
        name
        identifier
        identifierHeppaStatus
        activeIdentifier
        feiid

        westernRegistryNumber
        icelandicRegistryNumber

        ponyType
        currentSeasonLicenceValid

        dateOfBirth
        gender
        breedingStudbookId
        breedingStudbook @client {
          id
          nameShort
        }
        breedId
        breed @client {
          id
          nameShort
          name
        }
        sireName
        damName
        damSireName
        birthCountryId
        birthCountry @client {
          id
          name
        }
        ownerName
      }
    `
  }
});

export function overwriteHorseData({ horses, registrationHorses }) {
  return horses.map(h => {
    const overwrite = registrationHorses.find(rh => rh.horseId == h.id);
    if (overwrite) {
      if (overwrite.horseName) {
        h.name = overwrite.horseName;
      }
    }
    return h;
  });
}
</script>
